import React, { useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { Chip, SearchableSelect, Text } from '~/components/core';
import { Feather } from '~/components/core/Icons';
import { periodLabel, STRATEGY_PERIODS, StrategyPeriod } from '~/constants/strategyConstants';
import { useStyles } from '~/hooks';
import { useTheme } from '~/hooks/useTheme';
import { FONT_SIZE_S } from '~/constants/typography';
import { scaleSize } from '~/constants/scale';
import { Theme } from '~/theme';

import { CreateStrategyWizardApi } from '../../../hooks/useCreateStrategyWizard';

type Props = {
    wizard: CreateStrategyWizardApi;
};

// Step 1: pick what to trade. Strategies have no user-facing name — the list and detail derive
// their title from the market + trade parameters.
export default function StepMarket({ wizard }: Props) {
    const styles = useStyles(stylesGenerator);
    const theme = useTheme();
    const [pickerOpen, setPickerOpen] = useState(false);

    return (
        <View>
            <Text style={styles.fieldLabel}>instrument</Text>
            <Pressable style={styles.selectField} onPress={() => setPickerOpen(true)}>
                <Text
                    noTranslate={!!wizard.instrument}
                    style={StyleSheet.flatten([
                        styles.selectValue,
                        !wizard.instrument ? styles.selectPlaceholder : null,
                    ])}
                >
                    {wizard.instrument ? wizard.instrumentLabel(wizard.instrument) : 'ai-strategy-pick-instrument'}
                </Text>
                <Feather name="chevron-down" size={scaleSize(16)} style={styles.selectChevron} />
            </Pressable>

            <Text style={styles.fieldLabel}>period</Text>
            <View style={styles.chipsRow}>
                {STRATEGY_PERIODS.map((p) => (
                    <Chip
                        key={p}
                        label={periodLabel(p)}
                        selected={wizard.period === p}
                        onPress={() => wizard.setPeriod(p as StrategyPeriod)}
                        containerStyle={styles.chip}
                    />
                ))}
            </View>

            <Text style={styles.fieldLabel}>side</Text>
            <View style={styles.chipsRow}>
                <Chip
                    label="buy"
                    selected={wizard.side === 'buy'}
                    color={wizard.side === 'buy' ? theme.text_upPrice : undefined}
                    onPress={() => wizard.setSide('buy')}
                    containerStyle={styles.chip}
                />
                <Chip
                    label="sell"
                    selected={wizard.side === 'sell'}
                    color={wizard.side === 'sell' ? theme.text_downPrice : undefined}
                    onPress={() => wizard.setSide('sell')}
                    containerStyle={styles.chip}
                />
            </View>
            <Text style={styles.sideHint}>ai-strategy-side-hint</Text>

            <SearchableSelect
                visible={pickerOpen}
                onClose={() => setPickerOpen(false)}
                title="instrument"
                options={wizard.instrumentOptions}
                selectedValue={wizard.instrument}
                onSelect={wizard.setInstrument}
            />
        </View>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        fieldLabel: {
            fontSize: FONT_SIZE_S,
            color: theme.text,
            marginTop: scaleSize(12),
            marginBottom: scaleSize(6),
        },
        selectField: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderWidth: 1,
            borderColor: theme.equityValue,
            borderRadius: scaleSize(8),
            backgroundColor: theme.backgroundSecondary,
            paddingHorizontal: scaleSize(10),
            paddingVertical: scaleSize(10),
        },
        selectValue: {
            fontSize: FONT_SIZE_S,
            color: theme.text,
        },
        selectPlaceholder: {
            color: theme.textSecondary,
        },
        selectChevron: {
            color: theme.textSecondary,
        },
        chipsRow: {
            flexDirection: 'row',
            flexWrap: 'wrap',
        },
        chip: {
            marginRight: scaleSize(8),
            marginBottom: scaleSize(4),
        },
        sideHint: {
            fontSize: FONT_SIZE_S,
            color: theme.textSecondary,
            marginTop: scaleSize(8),
        },
    });
